import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Store, Tag, Sliders, Bell, LogOut, ShieldCheck, DollarSign,
  Users, TrendingUp, Search, UserCheck, Trash2, Plus, Activity, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiError, setToken, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/admin/vendors", icon: Store, label: "Vendors" },
  { to: "/admin/plans", icon: Package, label: "Plans" },
  { to: "/admin/coupons", icon: Tag, label: "Coupons" },
  { to: "/admin/announcements", icon: Bell, label: "Announcements" },
  { to: "/admin/audit", icon: Activity, label: "Audit Log" },
];

export default function AdminPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (user && user.role !== "super_admin") return <Navigate to="/" />;
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex">
      <aside className="w-64 shrink-0 border-r border-slate-800 flex flex-col fixed h-screen bg-[#0B0F17]">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-slate-800 font-display font-bold text-lg">
          <div className="h-8 w-8 rounded-lg bg-indigo-500 grid place-items-center"><ShieldCheck className="h-5 w-5" /></div>
          Platform
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-indigo-500/15 text-indigo-300" : "text-slate-400 hover:text-white hover:bg-slate-800/60"}`}>
              <n.icon className="h-4 w-4" />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-2 text-sm text-slate-400">{user?.email}</div>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 w-full">
            <LogOut className="h-4 w-4" />Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 p-8">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="vendors" element={<Vendors />} />
          <Route path="plans" element={<Plans />} />
          <Route path="coupons" element={<Coupons />} />
          <Route path="announcements" element={<Announcements />} />
          <Route path="audit" element={<AuditLog />} />
        </Routes>
      </main>
    </div>
  );
}

function Head({ title, sub, children }) {
  return (
    <div className="flex items-end justify-between mb-8">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>
        {sub && <p className="text-slate-400 mt-1">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-6 rounded-xl bg-slate-900/60 border border-slate-800">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
        <Icon className={`h-5 w-5 ${accent}`} />
      </div>
      <div className="font-display text-3xl font-bold mt-3">{value}</div>
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;
  return (
    <>
      <Head title="Platform Overview" sub="Marketplace health at a glance" />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat icon={DollarSign} label="MRR" value={inr(d.mrr)} accent="text-emerald-400" />
        <Stat icon={TrendingUp} label="Platform GMV" value={inr(d.platform_gmv)} accent="text-indigo-400" />
        <Stat icon={Store} label="Active Vendors" value={d.active_vendors} accent="text-blue-400" />
        <Stat icon={Activity} label="Churn Rate" value={`${d.churn_rate}%`} accent="text-amber-400" />
      </div>
      <div className="grid md:grid-cols-3 gap-6 mt-6">
        <Stat icon={Store} label="Total Vendors" value={d.total_vendors} accent="text-slate-400" />
        <Stat icon={UserCheck} label="Pending Approval" value={d.pending_vendors} accent="text-amber-400" />
        <Stat icon={Package} label="Total Orders" value={d.total_orders} accent="text-slate-400" />
      </div>
    </>
  );
}

const statusColor = {
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  suspended: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  deactivated: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

function Vendors() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [plans, setPlans] = useState([]);
  const { applyAuth } = useAuth();
  const navigate = useNavigate();

  const load = () => api.get("/admin/vendors", { params: { q: q || undefined, status } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [q, status]);
  useEffect(() => { api.get("/admin/plans").then((r) => setPlans(r.data)); }, []);

  const setVendorStatus = async (id, s) => {
    try { await api.patch(`/admin/vendors/${id}/status`, { status: s }); toast.success(`Vendor ${s}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const changePlan = async (id, plan_id) => {
    try { await api.patch(`/admin/vendors/${id}/plan`, { plan_id }); toast.success("Plan updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const impersonate = async (id) => {
    try {
      const { data } = await api.post(`/admin/vendors/${id}/impersonate`);
      setToken(data.token);
      applyAuth({ token: data.token, user: { ...data.user, role: "vendor_admin", vendor_id: id }, vendor: data.vendor });
      toast.info(`Impersonating ${data.vendor.business_name}`);
      navigate("/vendor");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <Head title="Vendors" sub="Approve, suspend and manage store accounts" />
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-3 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors…" data-testid="vendor-search" className="pl-9 bg-slate-900 border-slate-700 text-white" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 bg-slate-900 border-slate-700 text-white" data-testid="vendor-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "pending", "approved", "suspended", "deactivated"].map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-xl border border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase tracking-wider">
            <tr>{["Business", "Slug", "Status", "Plan", "Revenue", "Products", "Actions"].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.id} className="border-t border-slate-800 hover:bg-slate-900/50" data-testid={`vendor-row-${v.slug}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-white">{v.business_name}</div>
                  <div className="text-slate-500 text-xs">{v.contact_email}</div>
                </td>
                <td className="px-4 py-3 font-mono text-slate-400">/{v.slug}</td>
                <td className="px-4 py-3"><Badge variant="outline" className={statusColor[v.status]}>{v.status}</Badge></td>
                <td className="px-4 py-3">
                  <Select value={v.plan_id || ""} onValueChange={(pid) => changePlan(v.id, pid)}>
                    <SelectTrigger className="w-32 h-8 bg-slate-900 border-slate-700 text-white text-xs" data-testid={`vendor-plan-${v.slug}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-slate-300">{inr(v.revenue)}</td>
                <td className="px-4 py-3 text-slate-300">{v.product_count}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    {v.status !== "approved" && <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => setVendorStatus(v.id, "approved")} data-testid={`approve-${v.slug}`}>Approve</Button>}
                    {v.status === "approved" && <Button size="sm" variant="outline" className="h-8 border-rose-500/40 text-rose-300 hover:bg-rose-500/10 bg-transparent text-xs" onClick={() => setVendorStatus(v.id, "suspended")} data-testid={`suspend-${v.slug}`}>Suspend</Button>}
                    <Button size="sm" variant="outline" className="h-8 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 text-xs" onClick={() => impersonate(v.id)} data-testid={`impersonate-${v.slug}`}><UserCheck className="h-3.5 w-3.5 mr-1" />Login as</Button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No vendors found</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Plans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", price: "", max_products: "", max_orders: "", billing_cycle: "monthly", advanced_analytics: false });
  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post("/admin/plans", { ...f, price: Number(f.price), max_products: Number(f.max_products), max_orders: Number(f.max_orders), features: { advanced_analytics: f.advanced_analytics, csv_export: true, own_payment_keys: false } });
      toast.success("Plan created"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/plans/${id}`); toast.success("Deleted"); load(); };
  return (
    <>
      <Head title="Subscription Plans" sub="Tier limits & feature flags">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-indigo-500 hover:bg-indigo-600" data-testid="new-plan-btn"><Plus className="h-4 w-4 mr-1" />New plan</Button></DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700 text-white">
            <DialogHeader><DialogTitle>Create plan</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[["name", "Name"], ["price", "Price (INR)"], ["max_products", "Max products"], ["max_orders", "Max orders/mo"]].map(([k, l]) => (
                <div key={k}><Label className="text-slate-400">{l}</Label><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="bg-slate-800 border-slate-700" data-testid={`plan-${k}`} /></div>
              ))}
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.advanced_analytics} onChange={(e) => setF({ ...f, advanced_analytics: e.target.checked })} data-testid="plan-analytics-flag" /> Advanced analytics feature flag</label>
            </div>
            <DialogFooter><Button onClick={create} className="bg-indigo-500 hover:bg-indigo-600" data-testid="plan-save">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </Head>
      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((p) => (
          <div key={p.id} className="p-6 rounded-xl bg-slate-900/60 border border-slate-800" data-testid={`plan-card-${p.name.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-semibold">{p.name}</h3>
              <button onClick={() => del(p.id)} className="text-slate-500 hover:text-rose-400" data-testid={`plan-delete-${p.name.toLowerCase()}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="font-display text-3xl font-bold mt-3">{inr(p.price)}<span className="text-sm text-slate-500 font-normal">/{p.billing_cycle}</span></div>
            <ul className="mt-4 space-y-2 text-sm text-slate-400">
              <li>{p.max_products} products</li>
              <li>{p.max_orders} orders / month</li>
              <li>{p.storage_cap_gb} GB storage</li>
              <li className={p.features?.advanced_analytics ? "text-emerald-400" : "text-slate-600"}>{p.features?.advanced_analytics ? "✓" : "✕"} Advanced analytics</li>
            </ul>
          </div>
        ))}
      </div>
    </>
  );
}

function Coupons() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ code: "", discount_percent: 10 });
  const load = () => api.get("/admin/coupons").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/coupons", f); toast.success("Coupon created"); setF({ code: "", discount_percent: 10 }); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <>
      <Head title="Platform Coupons" sub="Discounts for vendor plan signups" />
      <div className="flex gap-3 mb-6 max-w-lg">
        <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="CODE" className="bg-slate-900 border-slate-700 text-white font-mono" data-testid="coupon-code" />
        <Input type="number" value={f.discount_percent} onChange={(e) => setF({ ...f, discount_percent: Number(e.target.value) })} className="w-24 bg-slate-900 border-slate-700 text-white" data-testid="coupon-discount" />
        <Button onClick={create} className="bg-indigo-500 hover:bg-indigo-600" data-testid="coupon-create"><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((c) => (
          <div key={c.id} className="p-5 rounded-xl bg-slate-900/60 border border-slate-800 flex items-center justify-between" data-testid={`coupon-${c.code}`}>
            <div><div className="font-mono font-semibold text-lg">{c.code}</div><div className="text-slate-500 text-sm">{c.discount_percent}% off</div></div>
            <button onClick={async () => { await api.delete(`/admin/coupons/${c.id}`); load(); }} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-slate-500">No coupons yet.</div>}
      </div>
    </>
  );
}

function Announcements() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", message: "" });
  const load = () => api.get("/admin/announcements").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/announcements", f); toast.success("Broadcast sent to all vendors"); setF({ title: "", message: "" }); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <>
      <Head title="Broadcast Announcements" sub="Messages shown to all vendors" />
      <div className="max-w-lg space-y-3 mb-8">
        <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" className="bg-slate-900 border-slate-700 text-white" data-testid="ann-title" />
        <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Message…" rows={3} className="w-full rounded-lg bg-slate-900 border border-slate-700 text-white p-3 text-sm" data-testid="ann-message" />
        <Button onClick={create} className="bg-indigo-500 hover:bg-indigo-600" data-testid="ann-send"><Bell className="h-4 w-4 mr-1" />Broadcast</Button>
      </div>
      <div className="space-y-3">
        {rows.map((a) => (
          <div key={a.id} className="p-5 rounded-xl bg-slate-900/60 border border-slate-800 flex items-start justify-between">
            <div><div className="font-semibold">{a.title}</div><div className="text-slate-400 text-sm mt-1">{a.message}</div></div>
            <button onClick={async () => { await api.delete(`/admin/announcements/${a.id}`); load(); }} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </>
  );
}

function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setRows(r.data)); }, []);
  return (
    <>
      <Head title="Audit Log" sub="Impersonation & support actions" />
      <div className="rounded-xl border border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase tracking-wider"><tr>{["Action", "Vendor", "Admin", "When"].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="border-t border-slate-800">
                <td className="px-4 py-3"><Badge variant="outline" className="bg-indigo-500/15 text-indigo-300 border-indigo-500/30">{l.action}</Badge></td>
                <td className="px-4 py-3 text-slate-300">{l.vendor_name}</td>
                <td className="px-4 py-3 text-slate-400">{l.super_admin_email}</td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">{new Date(l.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-500">No actions logged yet</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
